/**
 * カードの回答（種別ごとの回答型の和）。採点済みのカードに戻ったとき、入力を復元するために使う。
 * 判別は `kind`。predict-output だけは歴史的に kind が回答そのもの（output / compile-error / runtime-error）。
 */
import type { ChoiceResult, JudgeResult } from '../../lib/judge';
import type { MemoAnswer as LibMemoAnswer } from '../../lib/cards';
import type { PredictAnswer } from './predict-output';

export type { PredictAnswer };

/** 穴埋め。method は照合で決まったか（local）、実行して決まったか（sandbox） */
export interface FillBlankAnswer {
  kind: 'fill-blank';
  values: Record<number, string>;
  perBlank: Record<number, boolean>;
  method: 'local' | 'sandbox';
  /** sandbox で判定したときのコンパイラ出力（CE のとき表示する） */
  compileOutput?: string;
}

/** 並べ替え。使った行の文字列の列（judge.md §4.2） */
export interface ParsonsAnswer {
  kind: 'parsons';
  lines: string[];
}

/** choice / reduce / complexity-quiz。selected は YAML の並び順の添字 */
export interface ChoiceAnswer {
  kind: 'choice';
  selected: number[];
  result: ChoiceResult;
}

/** 書いて AC。judge はケースごとの結果 */
export interface WriteAcAnswer {
  kind: 'write-ac';
  code: string;
  judge: JudgeResult;
}

/** 可視化ステップ。checkpoint ごとに選んだ添字 */
export interface VizStepAnswer {
  kind: 'viz-step';
  answers: number[];
}

/** 暗記カード（card-schema.md §3.9）。answer は src/lib/cards の MemoAnswer、text は入力欄の文字 */
export interface MemoCardAnswer {
  kind: 'memo';
  answer: LibMemoAnswer;
  text: string;
}

export type CardAnswer =
  | PredictAnswer
  | FillBlankAnswer
  | ParsonsAnswer
  | ChoiceAnswer
  | WriteAcAnswer
  | VizStepAnswer
  | MemoCardAnswer;

export function asPredictAnswer(a: CardAnswer | undefined): PredictAnswer | undefined {
  return a && (a.kind === 'output' || a.kind === 'compile-error' || a.kind === 'runtime-error')
    ? a
    : undefined;
}

export function asFillBlankAnswer(a: CardAnswer | undefined): FillBlankAnswer | undefined {
  return a?.kind === 'fill-blank' ? a : undefined;
}

export function asParsonsAnswer(a: CardAnswer | undefined): ParsonsAnswer | undefined {
  return a?.kind === 'parsons' ? a : undefined;
}

export function asChoiceAnswer(a: CardAnswer | undefined): ChoiceAnswer | undefined {
  return a?.kind === 'choice' ? a : undefined;
}

export function asWriteAcAnswer(a: CardAnswer | undefined): WriteAcAnswer | undefined {
  return a?.kind === 'write-ac' ? a : undefined;
}

export function asVizStepAnswer(a: CardAnswer | undefined): VizStepAnswer | undefined {
  return a?.kind === 'viz-step' ? a : undefined;
}

export function asMemoAnswer(a: CardAnswer | undefined): MemoCardAnswer | undefined {
  return a?.kind === 'memo' ? a : undefined;
}
